import { Component, Input, OnInit } from '@angular/core';
import { isSameModel } from '@app/functions';
import { FileHelperService } from '@app/services';
import { BaseForm } from '@forms/base.form';
import { BenefitCategory } from '@models/benefits/benefit-category.model';
import { BenefitProvider } from '@models/benefits/benefit-provider.model';
import { Benefit } from '@models/benefits/benefit.model';
import { FeatureService } from '@app/services/feature.service';
import { FeatureFlag } from '@app/enums';

type premiumSplitTypes = 'companyFull' | 'employeeFull' | 'custom';

@Component({
    selector: 'app-form-benefit',
    templateUrl: './benefit.template.html',
})
export class BenefitForm extends BaseForm implements OnInit {
    @Input()
    benefit: Benefit = new Benefit({
        providerId: null,
    });

    benefitCategories: BenefitCategory[];
    benefitProviders: BenefitProvider[];
    isSameModel = isSameModel;

    hasBenefitsPayroll = false;
    premiumSplit: premiumSplitTypes = 'companyFull';

    constructor(
        private fileHelper: FileHelperService,
        private featureService: FeatureService
    ) {
        super();

        BenefitCategory.orderBy('name', 'ASC')
            .get()
            .then(([benefitCategories]) => {
                this.benefitCategories = benefitCategories;
            });

        BenefitProvider.orderBy('name', 'ASC')
            .all()
            .then(([benefitProviders]) => {
                this.benefitProviders = benefitProviders;
            });
    }

    async ngOnInit(): Promise<void> {
        this.hasBenefitsPayroll = await this.featureService.has(FeatureFlag.benefitsPayroll);

        if (this.hasBenefitsPayroll) {
            if (this.benefit.splitPercentage == null || this.benefit.splitPercentage === 100) {
                this.premiumSplit = 'companyFull';
            } else if (this.benefit.splitPercentage === 0) {
                this.premiumSplit = 'employeeFull';
            } else {
                this.premiumSplit = 'custom';
            }
        }
    }

    updateSplitPercentage(updatedPremiumSplit: premiumSplitTypes): void {
        if (updatedPremiumSplit === 'companyFull') {
            this.benefit.splitPercentage = 100;
        } else if (updatedPremiumSplit === 'employeeFull') {
            this.benefit.splitPercentage = 0;
        }
    }

    setBenefit(benefit: Benefit): void {
        this.benefit = benefit;
    }

    async saveBooklet(file: File): Promise<void> {
        if (!file) {
            this.benefit.file = null;
            return;
        }

        const storedFile = await this.fileHelper.store(file);
        this.benefit.file = storedFile;
    }

    getDefaultDescription(): string {
        switch (this.benefit.benefitCategory.name) {
            case 'Life Insurance':
                return "Life insurance provides financial assistance in the event of a plan member's death. See booklet for details.";
            case 'Accidental Death and Dismemberment':
                return 'AD&D provides financial assistance if tragedy strikes and a plan member dies as the result of an accident. It also provides coverage if an accident results in the loss of a limb, sight or hearing. Some additional benefits include rehabilitation, spousal occupation re-training and a daycare benefit. See booklet for details.';
            case 'Dependent Life Insurance':
                return "Dependent Life insurance provides a life insurance benefit for a plan member's spouse and dependent children. See booklet for details.";
            case 'Long Term Disability':
                return 'LTD coverage provides income when a plan member is disabled and unable to perform the duties of his or her own occupation. See booklet for details.';
            case 'Short Term Disability':
                return 'Short Term Disability coverage provides income when a plan member is unable to work due to hospitalization, accident or sickness, up to 16, 17, or 26 weeks. See booklet for details.';
            case 'Critical Illness':
                return 'Critical Illness is designed to protect plan members against the economic hardship that can result from serious health problems. A lump sum is paid to the policyholder after diagnosis of one of the covered illnesses. See booklet for details.';
            case 'Extended Health Services':
                return 'EHC provides a wide range of coverage to supplement provincial health care plans, including Drug Plans, Vision Care, and Travel. See booklet for details.';
            case 'Dental':
                return 'Dental Care plans vary but can be broken down into three categories: Basic Services (cleanings, fillings, extractions), Major Restorative Services (dentures, crowns, bridges) and Orthodontic Services. See booklet for details.';
            case 'Health Care Spending Account (HCSA)':
                return 'HCSAs are an affordable way to help plan members obtain services they require without the hardships of extensive out-of-pocket expenses. This is an individual employee account that provides reimbursement for eligible health care expenses or other benefits that are not covered under provincial health insurance plans or your current plan. See booklet for details.';
            case 'Employee Assistance Program':
                return 'An EAP offers private and confidential counselling services to assist employees coping with personal or work-related stresses. Counselling is provided in the form of over-the-phone consultation often including psychological, addiction, marriage, legal, and financial counselling. See booklet for details.';
            case 'Wellness Spending Account (WSA)':
                return 'WSA is a spending account funded by your employer that you can use to pay for a range of items not covered by your group benefit plan or provincial health plan.';
            default:
                return '';
        }
    }
}
